import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { resolveBilledFrom, type SellerRegistration } from '../../../utils/invoiceAddress';
import { mfetch, MemberApiError, type MemberPack } from '../api';
import PackUsageCard from '../components/PackUsageCard';
import Icon from '../design/Icon';
import { inr, fmtDateShort } from '../design/atoms';
import DuesCard from '../components/DuesCard';

/* SPEC 12 Membership & billing — current plan card · add-ons · payment
   history with PDF invoice download (generation logic preserved). */

interface BillingPaymentRecord {
  id: string;
  amount: number;
  paymentMethod: string;
  paymentDate: string;
  discountAmount: number;
  taxAmount: number;
  taxPercent: number;
  notes: string | null;
}

interface PurchaseLineItem {
  id: string;
  itemType: string;
  name: string;
  price: string | number;
  quantity: number;
  gstPercent: string | number;
  discount: string | number;
}

interface PurchasePaymentRecord {
  id: string;
  amount: string | number;
  paymentMethod: string;
  paymentDate: string;
  notes: string | null;
}

interface PurchaseItem {
  id: string;
  gym: { id: string; name: string; displayName: string; address: string | null; phoneNumbers: string[] | null; email: string | null; gstin: string | null; includeGstinInInvoices?: boolean; registeredAddress: string | null };
  branch?: { id: string; name: string; displayName: string; gstin: string | null; includeGstinInInvoices?: boolean | null } | null;
  invoiceGstin?: string | null;
  invoiceGstinEnabled?: boolean | null;
  documentType?: string;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  member: { id: string; fullName: string | null; displayName: string | null; email: string | null; phoneNumber: string | null; address: string | null; city: string | null; pincode: string | null };
  invoiceId: string | null;
  status: string;
  paymentStatus: string;
  subtotal: string | number;
  taxAmount: string | number;
  discountAmount: string | number;
  totalAmount: string | number;
  paidAmount: string | number;
  pendingAmount: string | number;
  dueDate: string | null;
  notes: string | null;
  createdAt: string;
  lineItems: PurchaseLineItem[];
  payments: PurchasePaymentRecord[];
}

interface BillingItem {
  id: string;
  /** The sale it came from — opens its full invoice. Null for a legacy membership (generated here). */
  purchaseId?: string | null;
  gym: { id: string; name: string; displayName: string; address: string | null; phoneNumbers: string[] | null; email: string | null; gstin: string | null; includeGstinInInvoices?: boolean; registeredAddress: string | null; gstRegistrations?: SellerRegistration[] };
  plan: { id: string; name: string; price: number | null; durationMonths: number | null };
  member: { id: string; fullName: string | null; displayName: string | null; email: string | null; phoneNumber: string | null; phoneNo: string | null; address: string | null; city: string | null; pincode: string | null };
  status: string | null;
  startDate: string | null;
  endDate: string | null;
  purchaseDate: string;
  invoiceId: string | null;
  invoiceGstin?: string | null;
  documentType?: string;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  totalAmount: string | null;
  discountAmount: string | null;
  discountPercent: number | null;
  taxPercent: number | null;
  taxAmount: string | null;
  baseAmount: string | null;
  paymentMethod: string | null;
  notes: string | null;
  paymentNotes: string | null;
  paymentStatus: string;
  paidAmount: string | null;
  pendingAmount: string | null;
  dueDate: string | null;
  membershipPayments: BillingPaymentRecord[];
}

interface LayoutContext {
  gyms: { id: string; name: string; displayName: string }[];
  activeGymId: string | null;
  loading: boolean;
}

/** Date-range filters — mirror the check-in history's vocabulary. */
const RANGES = [
  { key: '1y', label: 'This year', days: 365 },
  { key: '2y', label: '2 years', days: 730 },
  { key: 'all', label: 'All time', days: null as number | null },
];

// Helper function to convert number to words
function numberToWords(num: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  if (num === 0) return 'Zero';
  if (num < 10) return ones[num];
  if (num < 20) return teens[num - 10];
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
  if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 ? ' ' + numberToWords(num % 100) : '');
  if (num < 100000) return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 ? ' ' + numberToWords(num % 1000) : '');
  if (num < 10000000) return numberToWords(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 ? ' ' + numberToWords(num % 100000) : '');
  return numberToWords(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 ? ' ' + numberToWords(num % 10000000) : '');
}

// Builds the GST tax rows for the inline PDF templates from the immutable
// sale-time snapshot. A bill of supply shows no tax rows; a tax invoice shows
// IGST for inter-state sales, or CGST + SGST for intra-state sales. Falls back
// to a single combined row when the split amounts are unavailable (legacy rows).
function buildTaxRowsHTML(opts: {
  isBillOfSupply: boolean;
  taxAmount: number;
  igst: number;
  cgst: number;
  sgst: number;
  combinedLabel: string;
}): string {
  const { isBillOfSupply, taxAmount, igst, cgst, sgst, combinedLabel } = opts;
  if (isBillOfSupply || taxAmount <= 0) return '';
  const rowStyle = 'display: flex; justify-content: space-between; margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb;';
  const labelStyle = 'font-size: 10px; color: #6b7280; font-weight: 600;';
  const valueStyle = 'font-size: 10px; color: #111827; font-weight: 600;';
  const fmt = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2 });
  const row = (label: string, amount: number) =>
    `<div style="${rowStyle}"><span style="${labelStyle}">${label}</span><span style="${valueStyle}">₹${fmt(amount)}</span></div>`;
  if (igst > 0) return row('IGST', igst);
  if (cgst > 0 || sgst > 0) return row('CGST', cgst) + row('SGST', sgst);
  return row(combinedLabel, taxAmount);
}

function isoToYmd(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const PAY_STATUS_TAG: Record<string, { label: string; cls: string }> = {
  paid: { label: 'Paid', cls: 'tag sage' },
  partial: { label: 'Partial', cls: 'tag gold' },
  due: { label: 'Due', cls: 'tag clay' },
};

export default function MemberBillingPage() {
  const { token } = useMemberAuth();
  const { gyms, activeGymId, loading: gymsLoading } = useOutletContext<LayoutContext>();
  const navigate = useNavigate();

  const [items, setItems] = useState<BillingItem[]>([]);
  const [purchases, setPurchases] = useState<PurchaseItem[]>([]);
  const [packs, setPacks] = useState<MemberPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // null = every gym the member belongs to (the unified cross-gym view).
  const [gymFilter, setGymFilter] = useState<string | null>(activeGymId);
  const [range, setRange] = useState('1y');

  const from = useMemo(() => {
    const days = RANGES.find(r => r.key === range)?.days;
    if (!days) return undefined;
    const d = new Date();
    d.setDate(d.getDate() - days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, [range]);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    Promise.all([
      mfetch(token, '/member/billing', { params: { gymId: gymFilter ?? undefined, from } }).then(r => {
        setItems(r.data || []);
        setPurchases(r.purchases || []);
      }),
      // Packs stay gym-scoped: a pack belongs to one gym.
      gymFilter
        ? mfetch(token, '/member/packs', { params: { gymId: gymFilter } })
            .then(r => setPacks(r.data?.packs || []))
            .catch(() => setPacks([]))
        : Promise.resolve(setPacks([])),
    ])
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load your billing'))
      .finally(() => setLoading(false));
  }, [token, gymFilter, from]);

  useEffect(load, [load]);

  const handleDownload = async (purchase: BillingItem) => {
    if (!purchase) return;

    try {
      // Calculate amounts
      const basePrice = Number(purchase.baseAmount || purchase.plan.price || 0);
      const discountPercent = Number(purchase.discountPercent || 0);
      const totalDiscount = purchase.discountAmount !== null && purchase.discountAmount !== undefined
        ? Number(purchase.discountAmount)
        : (basePrice * discountPercent) / 100;
      const subtotal = Math.max(0, basePrice - totalDiscount);
      const taxPercent = Number(purchase.taxPercent || 18);
      const taxAmount = Number(purchase.taxAmount || (subtotal * taxPercent) / 100);
      const totalAmount = Number(purchase.totalAmount || (subtotal + taxAmount));

      const paymentStatus = purchase.paymentStatus || 'paid';
      const paidAmt = Number(purchase.paidAmount || totalAmount);
      const pendingAmt = Number(purchase.pendingAmount || 0);
      const payments = purchase.membershipPayments || [];

      const isBillOfSupply = purchase.documentType === 'bill_of_supply';
      const sellerName = purchase.gym.displayName || purchase.gym.name;
      const billedFrom = resolveBilledFrom(purchase as any, sellerName);
      const taxRowsHTML = buildTaxRowsHTML({
        isBillOfSupply,
        taxAmount,
        igst: Number(purchase.igstAmount || 0),
        cgst: Number(purchase.cgstAmount || 0),
        sgst: Number(purchase.sgstAmount || 0),
        combinedLabel: `Tax (${taxPercent}%)`,
      });

      // Create invoice HTML (reusing the exact structure from ViewPurchaseModal)
      const invoiceHTML = `
        <div id="invoice-content" style="background: white; padding: 40px; font-family: 'Inter', sans-serif; font-size: 12px; min-height: auto; position: relative;">
          ${purchase.status === 'refund' ? `
            <div style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; z-index: 0; overflow: hidden;">
              <div style="color: rgba(239, 68, 68, 0.1); font-size: 120px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.2em; transform: rotate(-45deg); white-space: nowrap; border: 8px solid rgba(239, 68, 68, 0.1); padding: 12px 48px; border-radius: 40px;">
                Refunded
              </div>
            </div>
          ` : (paymentStatus === 'due' || paymentStatus === 'partial') ? `
            <div style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; z-index: 0; overflow: hidden;">
              <div style="color: ${paymentStatus === 'due' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)'}; font-size: 80px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.2em; transform: rotate(-45deg); white-space: nowrap; border: 8px solid ${paymentStatus === 'due' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)'}; padding: 12px 48px; border-radius: 40px;">
                ${paymentStatus === 'due' ? 'Payment Due' : 'Partial Payment'}
              </div>
            </div>
          ` : ''}

          <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid #f3f4f6;">
            <div>
              <h1 style="font-size: 24px; font-weight: 900; color: #111827; text-transform: uppercase; margin: 0;">${isBillOfSupply ? 'Bill of Supply' : 'Tax Invoice'}</h1>
              <p style="color: #6b7280; font-family: monospace; font-size: 10px; margin: 4px 0 0 0;">
                Invoice No: <span style="font-weight: bold; color: #111827;">${purchase.invoiceId || purchase.id.slice(0, 8).toUpperCase()}</span>
              </p>
            </div>
            <div style="text-align: right;">
              <p style="color: #9ca3af; font-size: 9px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.1em; margin: 0 0 2px 0;">Invoice Date</p>
              <p style="font-size: 12px; font-weight: bold; color: #111827; margin: 0;">
                ${new Date(purchase.purchaseDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-bottom: 24px;">
            <div>
              <p style="font-size: 9px; font-weight: 900; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.1em; margin: 0 0 4px 0;">Billed From</p>
              <p style="font-size: 16px; font-weight: bold; color: #111827; margin: 0;">${sellerName}</p>
              <div style="font-size: 10px; color: #4b5563; margin-top: 4px;">
                ${billedFrom.legalName ? `<p style="margin: 2px 0; color: #374151;">${billedFrom.legalName}</p>` : ''}
                ${billedFrom.address ? `<p style="margin: 2px 0;">${billedFrom.address}</p>` : ''}
                ${billedFrom.branchAddress ? `<p style="margin: 2px 0;">Branch: ${billedFrom.branchAddress}</p>` : ''}
                ${!isBillOfSupply && purchase.invoiceGstin ? `<p style="font-weight: bold; color: #111827; margin: 2px 0;">GSTIN: ${purchase.invoiceGstin}</p>` : ''}
              </div>
              <div style="font-size: 10px; color: #4b5563; margin-top: 8px;">
                <p style="margin: 2px 0;"><span style="font-weight: bold; color: #6b7280;">Email:</span> ${purchase.gym.email}</p>
                ${purchase.gym.phoneNumbers && purchase.gym.phoneNumbers.length > 0 ? `<p style="margin: 2px 0;"><span style="font-weight: bold; color: #6b7280;">Phone:</span> ${purchase.gym.phoneNumbers[0]}</p>` : ''}
              </div>
            </div>

            <div>
              <p style="font-size: 9px; font-weight: 900; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.1em; margin: 0 0 4px 0;">Billed To</p>
              <p style="font-size: 16px; font-weight: bold; color: #111827; margin: 0;">${purchase.member.displayName || purchase.member.fullName || 'N/A'}</p>
              <div style="font-size: 10px; color: #4b5563; margin-top: 4px;">
                ${purchase.member.address ? `<p style="margin: 2px 0;">${purchase.member.address}</p>` : ''}
                ${purchase.member.city ? `<p style="margin: 2px 0;">${purchase.member.city}${purchase.member.pincode ? ` - ${purchase.member.pincode}` : ''}</p>` : ''}
                ${(purchase.member.phoneNo || purchase.member.phoneNumber) ? `<p style="margin: 2px 0;"><span style="font-weight: bold; color: #6b7280;">Mob:</span> ${purchase.member.phoneNo || purchase.member.phoneNumber}</p>` : ''}
              </div>
              <div style="margin-top: 16px;">
                <p style="font-size: 9px; font-weight: 900; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.1em; margin: 0 0 4px 0;">Payment Method</p>
                <span style="display: inline-block; font-size: 10px; font-weight: bold; color: #111827; background: #f3f4f6; padding: 4px 8px; border-radius: 4px; text-transform: uppercase;">
                  ${purchase.paymentMethod || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          <div style="margin-bottom: 24px;">
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="border-bottom: 2px solid #111827;">
                  <th style="padding: 8px; font-size: 9px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.1em; color: #111827; text-align: left;">Description</th>
                  <th style="padding: 8px; font-size: 9px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.1em; color: #111827; text-align: center; width: 64px;">Qty</th>
                  <th style="padding: 8px; font-size: 9px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.1em; color: #111827; text-align: right; width: 96px;">Unit Price</th>
                  <th style="padding: 8px; font-size: 9px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.1em; color: #111827; text-align: right; width: 112px;">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr style="border-bottom: 1px solid #f3f4f6;">
                  <td style="padding: 12px 8px; vertical-align: top;">
                    <p style="font-size: 12px; font-weight: bold; color: #111827; margin: 0;">${purchase.plan.name}</p>
                    <p style="font-size: 9px; color: #6b7280; margin: 2px 0; font-weight: bold; text-transform: uppercase;">
                      ${purchase.plan.durationMonths || 1} ${purchase.plan.durationMonths === 1 ? 'Month' : 'Months'} Access Plan
                    </p>
                    <p style="font-size: 9px; color: #9ca3af; margin: 2px 0; font-style: italic;">
                      ${purchase.startDate ? new Date(purchase.startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'} - ${purchase.endDate ? new Date(purchase.endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                    </p>
                  </td>
                  <td style="padding: 12px 8px; text-align: center; font-weight: bold; color: #111827; vertical-align: top;">1.00</td>
                  <td style="padding: 12px 8px; text-align: right; font-weight: 500; color: #111827; vertical-align: top;">₹${basePrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td style="padding: 12px 8px; text-align: right; font-weight: bold; color: #111827; vertical-align: top;">₹${basePrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style="display: flex; justify-content: space-between; align-items: start; border-top: 1px solid #e5e7eb; padding-top: 24px;">
            <div style="width: 50%; padding-right: 32px;">
              <div style="margin-bottom: 24px;">
                <p style="font-size: 9px; font-weight: 900; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.1em; margin: 0 0 4px 0;">Amount in Words</p>
                <p style="font-size: 10px; font-weight: bold; color: #4b5563; text-transform: capitalize; margin: 0;">
                  ${numberToWords(Math.round(totalAmount))} Rupees Only
                </p>
              </div>
              ${(purchase.notes || purchase.paymentNotes) ? `
                <div style="margin-bottom: 24px;">
                  <p style="font-size: 9px; font-weight: 900; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.1em; margin: 0 0 4px 0;">Remarks</p>
                  <p style="font-size: 9px; color: #4b5563; font-style: italic; margin: 0;">
                    "${purchase.paymentNotes || purchase.notes}"
                  </p>
                </div>
              ` : ''}
              <div>
                <p style="font-size: 9px; font-weight: 900; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.1em; margin: 0 0 4px 0;">Terms & Conditions</p>
                <ul style="font-size: 8px; color: #6b7280; margin: 0; padding-left: 16px;">
                  <li style="margin: 2px 0;">Membership fees are non-refundable & non-transferable.</li>
                  <li style="margin: 2px 0;">Valid only for the period specified above.</li>
                  <li style="margin: 2px 0;">Please maintain gym decorum & safety protocols.</li>
                </ul>
              </div>
            </div>

            <div style="width: 40%; max-width: 280px;">
              <div style="background: #f9fafb; border-radius: 12px; padding: 16px;">
                <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                  <span style="font-size: 10px; color: #6b7280; font-weight: 600;">Subtotal</span>
                  <span style="font-size: 10px; color: #111827; font-weight: 600;">₹${basePrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                ${totalDiscount > 0 ? `
                  <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                    <span style="font-size: 10px; color: #16a34a; font-weight: 600;">Discount ${discountPercent > 0 ? `(${discountPercent}%)` : ''}</span>
                    <span style="font-size: 10px; color: #16a34a; font-weight: 600;">-₹${totalDiscount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb;">
                    <span style="font-size: 10px; color: #6b7280; font-weight: 600;">After Discount</span>
                    <span style="font-size: 10px; color: #111827; font-weight: 600;">₹${subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                ` : ''}
                ${taxRowsHTML}
                <div style="display: flex; justify-content: space-between; background: #111827; color: white; padding: 12px; border-radius: 8px; margin-top: 8px;">
                  <span style="font-size: 11px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.05em;">Total Amount</span>
                  <span style="font-size: 14px; font-weight: 900;">₹${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                ${(paymentStatus === 'partial' || paymentStatus === 'due') ? `
                <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e5e7eb;">
                  <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
                    <span style="font-size: 10px; color: #16a34a; font-weight: 700;">Amount Paid</span>
                    <span style="font-size: 10px; color: #16a34a; font-weight: 700;">₹${paidAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between;">
                    <span style="font-size: 10px; color: #d97706; font-weight: 700;">Pending Amount</span>
                    <span style="font-size: 10px; color: #d97706; font-weight: 700;">₹${pendingAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  ${purchase.dueDate ? `<p style="font-size: 9px; color: #ef4444; margin-top: 4px;">Due by: ${new Date(purchase.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>` : ''}
                </div>
                ` : ''}
              </div>
            </div>
          </div>
          ${payments.length > 0 ? `
          <div style="margin-top: 24px; padding-top: 16px; border-top: 1px solid #f3f4f6;">
            <p style="font-size: 9px; font-weight: 900; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.1em; margin: 0 0 8px 0;">Payment Receipts</p>
            ${payments.map((p: any, idx: number) => `
              <div style="display: flex; justify-content: space-between; padding: 8px; background: #f9fafb; border-radius: 6px; margin-bottom: 4px;">
                <div>
                  <span style="font-size: 9px; color: #6b7280; margin-right: 8px;">#${idx + 1}</span>
                  <span style="font-size: 10px; font-weight: 700; color: #111827;">₹${Number(p.amount).toFixed(2)}</span>
                  <span style="font-size: 9px; color: #6b7280; margin-left: 8px;">${p.paymentMethod} · ${new Date(p.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
                ${Number(p.taxAmount) > 0 ? `<span style="font-size: 9px; color: #6b7280;">GST: ₹${Number(p.taxAmount).toFixed(2)}</span>` : ''}
              </div>
            `).join('')}
          </div>
          ` : ''}
        </div>
      `;

      // Create a temporary div to hold the invoice HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = invoiceHTML;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      document.body.appendChild(tempDiv);

      const html2pdf = (await import('html2pdf.js')).default;

      const opt = {
        margin: 0.5,
        filename: `invoice-${purchase.invoiceId || purchase.id}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true
        },
        jsPDF: {
          unit: 'in',
          format: 'a4',
          orientation: 'portrait'
        } as const
      };

      const content = tempDiv.querySelector('#invoice-content') as HTMLElement;
      if (!content) throw new Error('Failed to find invoice content');

      await html2pdf().set(opt).from(content).save();
      toast.success('Invoice downloaded successfully');

      // Clean up
      document.body.removeChild(tempDiv);
    } catch (error: any) {
      console.error('Error generating PDF:', error);
      toast.error(error.message || 'Failed to download invoice');
    }
  };

  // ── Derived view data ──────────────────────────────────────────────────────
  const now = new Date();

  const currentPlan = useMemo(() => {
    const withDates = items.filter(m => m.startDate && m.endDate);
    const active = withDates.find(
      m => (m.status ?? '').toLowerCase() === 'active' && new Date(m.endDate!) >= now && new Date(m.startDate!) <= now
    );
    if (active) return { item: active, active: true };
    const latest = [...withDates].sort(
      (a, b) => new Date(b.endDate!).getTime() - new Date(a.endDate!).getTime()
    )[0];
    return latest ? { item: latest, active: false } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const historyRows = useMemo(() => {
    const rows: {
      key: string;
      name: string;
      date: string;
      paymentStatus: string;
      refunded: boolean;
      total: number;
      pending: number;
      /** Opens the sale's invoice page; a legacy membership downloads its generated receipt instead. */
      open: () => Promise<void>;
    }[] = [];
    const openInvoice = (purchaseId: string) => async () => navigate(`/app/billing/invoice/${purchaseId}`);
    for (const item of items) {
      rows.push({
        key: `m-${item.id}`,
        name: item.plan?.name ?? 'Membership',
        date: item.purchaseDate,
        paymentStatus: item.paymentStatus || 'paid',
        refunded: item.status === 'refund',
        total: Number(item.totalAmount || 0),
        pending: Number(item.pendingAmount || 0),
        open: item.purchaseId ? openInvoice(item.purchaseId) : () => handleDownload(item),
      });
    }
    for (const p of purchases) {
      const name = p.lineItems.length === 1
        ? p.lineItems[0].name
        : `${p.lineItems[0]?.name ?? 'Purchase'}${p.lineItems.length > 1 ? ` +${p.lineItems.length - 1} more` : ''}`;
      rows.push({
        key: `p-${p.id}`,
        name,
        date: p.createdAt,
        paymentStatus: p.paymentStatus || 'paid',
        refunded: p.status === 'refund',
        total: Number(p.totalAmount || 0),
        pending: Number(p.pendingAmount || 0),
        open: openInvoice(p.id),
      });
    }
    return rows.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, purchases]);


  const startDownload = async (key: string, download: () => Promise<void>) => {
    if (downloadingId) return;
    setDownloadingId(key);
    try {
      await download();
    } finally {
      setDownloadingId(null);
    }
  };

  if (!gymsLoading && !activeGymId) {
    return (
      <div className="mpage">
        <div className="dp dp-l" style={{ paddingTop: 8 }}>Membership</div>
        <div className="card flat"><div className="sub wrap">Join a gym to see your membership and payments.</div></div>
      </div>
    );
  }

  return (
    <div className="mpage">
      <div className="row" style={{ paddingTop: 8, gap: 10 }}>
        <button className="icon-btn" aria-label="Back" onClick={() => navigate(-1)}>
          <Icon n="left" s={18} c="var(--ink-2)" />
        </button>
        <div className="dp dp-l">Membership</div>
      </div>

      {token && activeGymId && <DuesCard token={token} gymId={gymFilter ?? activeGymId} />}

      {gyms.length > 1 && (
        <div className="hscroll">
          <button className={`chip${gymFilter === null ? ' on' : ''}`} onClick={() => setGymFilter(null)}>
            All gyms
          </button>
          {gyms.map(g => (
            <button key={g.id} className={`chip${gymFilter === g.id ? ' on' : ''}`} onClick={() => setGymFilter(g.id)}>
              {g.displayName || g.name}
            </button>
          ))}
        </div>
      )}

      <div className="hscroll">
        {RANGES.map(r => (
          <button key={r.key} className={`chip${range === r.key ? ' on' : ''}`} onClick={() => setRange(r.key)}>
            {r.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="col" style={{ gap: 12 }}>
          <div className="skel" style={{ height: 150, borderRadius: 'var(--r-m)' }} />
          <div className="skel" style={{ height: 64 }} />
          <div className="skel" style={{ height: 64 }} />
        </div>
      ) : error ? (
        <div className="card flat">
          <div className="sub wrap">{error}</div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={load}>Retry</button>
        </div>
      ) : (
        <>
          {/* Current plan */}
          {currentPlan && (() => {
            const { item, active } = currentPlan;
            const start = new Date(item.startDate!);
            const end = new Date(item.endDate!);
            const span = Math.max(1, end.getTime() - start.getTime());
            const elapsed = Math.min(1, Math.max(0, (now.getTime() - start.getTime()) / span));
            const pct = Math.round(elapsed * 100);
            const daysRemaining = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / 86400000));
            return (
              <div className="card tint3">
                <div className="between">
                  <div className="lbl-d">Current plan</div>
                  {active ? <span className="tag sage">Active</span> : <span className="tag">Expired</span>}
                </div>
                <div className="col" style={{ gap: 3, marginTop: 8 }}>
                  <div className="dp dp-m" style={{ whiteSpace: 'normal' }}>{item.plan?.name ?? 'Membership'}</div>
                  <div className="sub">
                    {fmtDateShort(isoToYmd(item.startDate!))} – {fmtDateShort(isoToYmd(item.endDate!))}
                  </div>
                </div>
                <div className="bar" style={{ marginTop: 14 }}>
                  <i style={{ width: `${pct}%` }} />
                </div>
                <div className="tiny" style={{ marginTop: 7 }}>
                  {daysRemaining} day{daysRemaining === 1 ? '' : 's'} remaining · {pct}% elapsed
                </div>
              </div>
            );
          })()}

          {/* Packs and sessions bought, with what each has been used for */}
          {packs.length > 0 && (
            <div className="col" style={{ gap: 9 }}>
              <div className="lbl" style={{ paddingLeft: 2 }}>Packs &amp; sessions</div>
              {packs.map(p => (
                <PackUsageCard key={p.id} pack={p} onInvoice={p.purchaseId ? () => navigate(`/app/billing/invoice/${p.purchaseId}`) : undefined} />
              ))}
            </div>
          )}

          {/* Payment history */}
          {historyRows.length > 0 ? (
            <div className="col" style={{ gap: 9 }}>
              <div className="lbl" style={{ paddingLeft: 2 }}>Payment history</div>
              <div className="card" style={{ padding: '5px 16px' }}>
                {historyRows.map(row => {
                  const tag = row.refunded
                    ? { label: 'Refunded', cls: 'tag clay' }
                    : PAY_STATUS_TAG[row.paymentStatus] ?? { label: row.paymentStatus, cls: 'tag' };
                  const busy = downloadingId === row.key;
                  return (
                    <button
                      key={row.key}
                      className="lrow"
                      disabled={busy || downloadingId !== null}
                      style={busy ? { opacity: .55 } : undefined}
                      onClick={() => startDownload(row.key, row.open)}
                    >
                      <div className="col" style={{ gap: 3, flex: 1, minWidth: 0 }}>
                        <div className="mid trunc">{row.name}</div>
                        <div className="row" style={{ gap: 7 }}>
                          <span className="tiny">{fmtDateShort(isoToYmd(row.date))}</span>
                          <span className={tag.cls}>{tag.label}</span>
                          {row.pending > 0 && !row.refunded && (
                            <span className="tiny" style={{ color: 'var(--clay)' }}>{inr(row.pending)} due</span>
                          )}
                        </div>
                      </div>
                      <span className="num" style={{ fontSize: 15 }}>{inr(row.total)}</span>
                      {busy
                        ? <div className="mspin" style={{ width: 16, height: 16, flex: '0 0 16px' }} />
                        : <Icon n="right" s={16} c="var(--ink-3)" />}
                    </button>
                  );
                })}
              </div>
              <div className="tiny" style={{ paddingLeft: 2 }}>Tap a payment to see its invoice.</div>
            </div>
          ) : !currentPlan && packs.length === 0 ? (
            <div className="card flat" style={{ textAlign: 'center', padding: 26 }}>
              <div className="mid">No billing records yet</div>
              <div className="sub wrap" style={{ marginTop: 4 }}>Your memberships and payments will show up here.</div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
